import React, { useCallback, useState } from 'react';
import { useFirebase } from './FirebaseProvider';
import { isSignInActionDisabled, runGoogleSignIn } from './chronovelaSignIn.controller';

export interface ChronovelaSignInScreenProps {
  /** Optional subtitle under the Chronovela wordmark. */
  subtitle?: string;
  /** Show staging compatibility banner above the card. */
  showStagingBanner?: boolean;
}

export function ChronovelaSignInScreen({
  subtitle = 'AI manuscript generation platform for authors.',
  showStagingBanner = false,
}: ChronovelaSignInScreenProps) {
  const { login, loading } = useFirebase();
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  const handleSignIn = useCallback(async () => {
    await runGoogleSignIn({
      login,
      signingIn,
      setSigningIn,
      setError: setSignInError,
    });
  }, [login, signingIn]);

  const disabled = isSignInActionDisabled(loading, signingIn);
  const statusLabel = loading
    ? 'Checking your session…'
    : signingIn
      ? 'Opening Google Sign-In…'
      : null;

  return (
    <div className="min-h-screen bg-cv-page text-cv-primary flex flex-col">
      {showStagingBanner && (
        <div className="bg-amber-600 text-black text-xs text-center py-1 font-semibold tracking-wide">
          STAGING — Original Engine Compatibility
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-72 bg-[radial-gradient(ellipse_at_top,rgba(180,154,99,0.12),transparent_60%)] pointer-events-none" />

        <div className="relative z-10 w-full max-w-md bg-cv-panel/80 border border-cv-border rounded-2xl shadow-2xl p-8 md:p-10 text-center">
          <p className="text-3xl font-black text-cv-gold tracking-tighter mb-2">Chronovela</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-cv-muted mb-6">
            Operated by Skald Haven Inc.
          </p>
          <p className="text-sm text-cv-secondary leading-relaxed mb-8">{subtitle}</p>

          {statusLabel && (
            <p className="text-xs text-cv-muted font-mono mb-4" role="status" aria-live="polite">
              {statusLabel}
            </p>
          )}

          {signInError && (
            <div
              className="mb-4 rounded-lg border border-cv-danger/40 bg-cv-danger/10 px-4 py-3 text-left text-sm text-cv-danger"
              role="alert"
            >
              {signInError}
            </div>
          )}

          <button
            type="button"
            onClick={() => void handleSignIn()}
            disabled={disabled}
            aria-busy={signingIn || loading}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-cv-interactive hover:bg-cv-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed text-cv-page rounded-xl font-bold text-sm uppercase tracking-wider transition-all shadow-lg shadow-black/30"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>

          <a
            href="/"
            className="inline-block mt-6 text-xs font-bold uppercase tracking-widest text-cv-muted hover:text-cv-primary transition-colors"
          >
            ← Back to landing page
          </a>
        </div>
      </div>
    </div>
  );
}

export default ChronovelaSignInScreen;
